/**
 * Regex validation against a string. Return whether a string is a decimal string.
 *
 * @param {string} [str=56.00,55] - decimal string
 * @param {string} [language="de"]
 * @returns {boolean}
 */
export const isDecimal = (str: string, language: string): boolean => {
  switch (language) {
    case "en":
      return RegExp(/^(\d{1,3}(,){0,1})*(\d{3})*(\.\d{2})?$/).test(str);
    default:
      return RegExp(/^(\d{1,3}(\.){0,1})*(\d{3})*(,\d{2})?$/).test(str);
  }
};

/**
 * Formats given number into a decimal string
 *
 * @param {number} amount - Given number
 * @param {string} [localeString="de"]
 * @param {boolean} [useGrouping=false] Optinal set grouping dot
 * @returns {string}
 */
export const formatDecimal = (
  amount: number,
  localeString = "de",
  useGrouping = false,
  fractionDigits = 2,
  maximumFractionDigits = 2
): string =>
  new Intl.NumberFormat(localeString, {
    style: "decimal",
    useGrouping: useGrouping,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: maximumFractionDigits
  }).format(amount);

/**
 * Formats given number into a curreny string
 *
 * @param {number} amount - Given number
 * @param {string} [localeString="de"]
 * @param {boolean} [useGrouping=false] Optinal set grouping dot
 * @param {string} [currency="EUR"]
 * @returns {string}
 */
export const formatCurrency = (
  amount: number,
  localeString = "de",
  useGrouping = false,
  currency = "EUR",
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  currencyDisplay: "narrowSymbol" | "symbol" | "code" | "name" = "code"
): string =>
  new Intl.NumberFormat("de", {
    style: "currency",
    currency: currency,
    currencyDisplay: currencyDisplay,
    useGrouping: useGrouping,
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits
  }).format(amount);

export const isValidPositiveNumber = (value: string | number): boolean => {
  const cleanedValue = value.toString().replace(/ /g, "");
  const num = Number(cleanedValue);
  if (isNaN(num)) return false;
  if (num <= 0) return false;

  return true;
};

export const isValidNumber = (val: string | number | undefined): boolean => {
  if (
    val === undefined ||
    (typeof val === "string" && val.replace(/\s+/g, " ") === "")
  )
    return false;
  return !isNaN(Number(val));
};
